import {useCallback} from 'react';

import {recordRumDurationVital} from '../datadogRum';
import {
  getDatadogRumViewTimingTimestamps,
  getRumViewStartTimestamp,
  hasRecordedRumTiming,
  recordFirstRumTiming,
  type UseFirstRumTimingOptions,
  useFirstTimingWhen,
} from '../datadogRumTimings';

export type WorkspaceLoadingVital =
  | 'workspace_init'
  | 'workspace_view_spec_loaded'
  | 'workspace_panel_bank_loaded'
  | 'workspace_run_selector_loaded';

export type WorkspaceLoadingIntervalVital =
  | 'workspace_init_to_view_spec'
  | 'workspace_view_spec_to_panel_bank'
  | 'workspace_view_spec_to_run_selector';

export type WorkspaceLoadingVitalName =
  | WorkspaceLoadingVital
  | WorkspaceLoadingIntervalVital;

export type WorkspaceLoadingVitalMeasurements = Partial<
  Record<WorkspaceLoadingVitalName, number>
>;

/** Window key Playwright reads via `page.evaluate`. */
export const WORKSPACE_LOADING_VITALS_WINDOW_KEY =
  '__WB_workspaceLoadingVitals' as const;

/**
 * Interval vitals answer "how long did this part of loading take?"
 *
 * Each entry is keyed by the loading stage vital that ends an interval:
 *
 * - `workspace_init_to_view_spec`: workspace shell -> view spec ready
 * - `workspace_view_spec_to_panel_bank`: view spec ready -> panels about to render
 * - `workspace_view_spec_to_run_selector`: view spec ready -> runs visible, interactable
 *
 * `startVitalName` is where the interval starts.
 * `intervalVitalName` is the `*_to_*` vital we emit when the end stage fires.
 *
 * Panel bank and run selector both start from view spec because neither one is
 * the parent of the other.
 */
const WORKSPACE_LOADING_INTERVAL_VITALS: Partial<
  Record<
    WorkspaceLoadingVital,
    {
      startVitalName: WorkspaceLoadingVital;
      intervalVitalName: WorkspaceLoadingIntervalVital;
    }
  >
> = {
  workspace_view_spec_loaded: {
    startVitalName: 'workspace_init',
    intervalVitalName: 'workspace_init_to_view_spec',
  },
  workspace_panel_bank_loaded: {
    startVitalName: 'workspace_view_spec_loaded',
    intervalVitalName: 'workspace_view_spec_to_panel_bank',
  },
  workspace_run_selector_loaded: {
    startVitalName: 'workspace_view_spec_loaded',
    intervalVitalName: 'workspace_view_spec_to_run_selector',
  },
};

export function getWorkspaceLoadingVitalMeasurements(): WorkspaceLoadingVitalMeasurements {
  if (typeof window === 'undefined') {
    return {};
  }

  return {...window[WORKSPACE_LOADING_VITALS_WINDOW_KEY]};
}

/**
 * Publishes workspace loading vital to window.__WB_workspaceLoadingVitals
 * These vitals are read by playwright in getWorkspaceLoadingVitalsFromPage
 */
function publishWorkspaceLoadingVitalForTests(
  name: WorkspaceLoadingVitalName,
  durationMs: number
) {
  if (typeof window === 'undefined') {
    return;
  }

  if (name === 'workspace_init') {
    window[WORKSPACE_LOADING_VITALS_WINDOW_KEY] = {};
  }

  const measurements =
    window[WORKSPACE_LOADING_VITALS_WINDOW_KEY] ??
    (window[WORKSPACE_LOADING_VITALS_WINDOW_KEY] = {});

  measurements[name] = durationMs;
}

function recordWorkspaceLoadingDurationVital(
  name: WorkspaceLoadingVitalName,
  startTime: number,
  durationMs: number
) {
  recordRumDurationVital(name, {
    startTime,
    duration: durationMs,
  });
  publishWorkspaceLoadingVitalForTests(name, durationMs);
}

/**
 * React lifecycle wrapper for `recordWorkspaceLoadingTiming`. This owns only
 * the `when` behavior; timestamp normalization, per-view dedupe, and duration
 * vital emission all stay in the plain recorder below.
 *
 * Workspace mount stages use `when: 'mount'` when they specifically mean
 * "the workspace UI committed, before the browser painted it."
 *
 * @param endVitalName - The workspace loading stage vital to record
 * @param options - `when` follows the same trigger shape as `useFirstRumTiming`
 * @returns A manual trigger for `when: 'manual'`
 */
export function useRecordWorkspaceLoading(
  endVitalName: WorkspaceLoadingVital,
  {when}: {when: UseFirstRumTimingOptions['when']}
) {
  const record = useCallback(() => {
    recordWorkspaceLoadingTiming(endVitalName);
  }, [endVitalName]);

  return useFirstTimingWhen(endVitalName, when, record);
}

/**
 * Records a workspace loading stage and the intervals it completes.
 *
 * The workspace route starts its RUM view before workspace mount records run,
 * so the stage can be written right away. If a stage timestamp is earlier than
 * the route start, clip it to the route start so the duration stays on the
 * intended route.
 *
 * @param endVitalName - The workspace loading stage vital to record
 */
export function recordWorkspaceLoadingTiming(
  endVitalName: WorkspaceLoadingVital
) {
  if (hasRecordedRumTiming(endVitalName)) {
    return;
  }

  const eventTimestamp = performance.timeOrigin + performance.now();
  const viewStartTimestamp = getRumViewStartTimestamp();
  const recordedTimestamp = Math.max(eventTimestamp, viewStartTimestamp);

  if (!recordFirstRumTiming(endVitalName, recordedTimestamp)) {
    return;
  }

  recordWorkspaceLoadingVitals(
    endVitalName,
    recordedTimestamp,
    viewStartTimestamp
  );
}

/**
 * Writes two duration vitals:
 * - the loading stage vital, measuring from route start to this stage
 * - the interval vital ending here, if we saw its start in this route
 *
 * If the interval's start happened on an older route, skip that interval
 * instead of mixing two routes.
 *
 * @param endVitalName - The loading stage vital to record
 * @param recordedTimestamp - The timestamp recorded for this stage
 */
function recordWorkspaceLoadingVitals(
  endVitalName: WorkspaceLoadingVital,
  recordedTimestamp: number,
  viewStartTimestamp: number
) {
  const timingTimestamps = getDatadogRumViewTimingTimestamps();

  const stageDurationMs = recordedTimestamp - viewStartTimestamp;
  recordWorkspaceLoadingDurationVital(
    endVitalName,
    viewStartTimestamp,
    stageDurationMs
  );

  const intervalVital = WORKSPACE_LOADING_INTERVAL_VITALS[endVitalName];
  if (intervalVital != null) {
    const intervalStartTimestamp = timingTimestamps.get(
      intervalVital.startVitalName
    );
    if (intervalStartTimestamp != null) {
      const intervalDurationMs = recordedTimestamp - intervalStartTimestamp;
      recordWorkspaceLoadingDurationVital(
        intervalVital.intervalVitalName,
        intervalStartTimestamp,
        intervalDurationMs
      );
    }
  }

  timingTimestamps.set(endVitalName, recordedTimestamp);
}
